import React from "react";
import { FormControl, InputLabel, useMediaQuery, Theme } from "@mui/material";
import { DropdownSectionData } from "./types";
import { DesktopDropdown } from "./DesktopDropdown";
import { MobileDropdown } from "./MobileDropdown";
import { SectionsList } from "./SectionsList";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { useSections } from "./hooks/useSections";
import { useDropdownState } from "./hooks/useDropdownState";

interface Props {
  label: string;
  defaultValue: string | number;
  onChange: (value: string | number) => void;
  sections: DropdownSectionData[];
  isFetching: boolean;
}

export const DropdownWithSections = ({
  label,
  defaultValue,
  onChange,
  sections,
  isFetching,
}: Props) => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const {
    filteredSections,
    allOptions,
    selectedLabel,
    searchQuery,
    setSearchQuery,
  } = useSections(sections, defaultValue);
  const state = useDropdownState({
    defaultValue,
    onChange,
    isMobile,
    setSearchQuery,
  });
  const { highlightedIndex, resetHighlightedIndex } = useKeyboardNavigation({
    isOpen: state.open,
    maxIndex: allOptions.length - 1,
    onSelect: (index) =>
      allOptions[index] && state.handleSelect(allOptions[index]),
    onClose: state.handleClose,
  });

  const sectionsList = (
    <SectionsList
      sections={filteredSections}
      isFetching={isFetching}
      searchQuery={searchQuery}
      selectedValue={isMobile ? state.tempSelectedValue : defaultValue}
      onSelect={state.handleSelect}
      highlightedIndex={highlightedIndex}
    />
  );

  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      {isMobile ? (
        <MobileDropdown
          label={label}
          selectedLabel={selectedLabel || ""}
          open={state.open}
          onToggle={state.handleToggle}
          onClose={state.handleClose}
          onConfirm={state.handleConfirm}
        >
          {sectionsList}
        </MobileDropdown>
      ) : (
        <DesktopDropdown
          anchorEl={state.anchorEl}
          setAnchorEl={state.setAnchorEl}
          open={state.open}
          selectedLabel={selectedLabel}
          searchQuery={searchQuery}
          label={label}
          onInputChange={(e) =>
            state.handleInputChange(e, resetHighlightedIndex)
          }
          onInputClick={() => state.setOpen(true)}
          onClose={state.handleClose}
        >
          {sectionsList}
        </DesktopDropdown>
      )}
    </FormControl>
  );
};
